import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  CircularProgress,
  Zoom,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  FilterList,
  PersonAdd,
  Refresh,
  Email,
  ViewList,
  ViewModule,
  Add,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import userService from '../../services/userService';
import AddUserDialog from './dialogs/AddUser';
import EditUser from './dialogs/EditUser';
import DeleteDialog from './dialogs/DeleteDialog';
import SuccessDialog from './dialogs/SuccessDialog';

// User Card Component for Grid View
const UserCard = ({ user, onEdit, onDelete, onResetPassword, t }) => {
  const theme = useTheme();
  
  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin':
        return {
          bgcolor: theme.palette.error.light + '20',
          color: theme.palette.error.main
        };
      case 'Manager':
        return {
          bgcolor: theme.palette.warning.light + '20',
          color: theme.palette.warning.main
        };
      case 'Staff':
        return {
          bgcolor: theme.palette.success.light + '20',
          color: theme.palette.success.main
        };
      default:
        return {
          bgcolor: theme.palette.grey[100],
          color: theme.palette.grey[700]
        };
    }
  }

  // Generate initials for avatar
  const getInitials = (username) => {
    return username ? username.charAt(0).toUpperCase() : '';
  };

  return (
    <Card 
      component={motion.div}
      whileHover={{ y: -5, boxShadow: theme.shadows[8] }}
      transition={{ duration: 0.2 }}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)'
      }}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        borderBottom: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)'
      }}>
        <Avatar
          src={user.image}
          sx={{
            width: 72,
            height: 72,
            mb: 1,
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.1)'
          }}
        >
          {getInitials(user.username)}
        </Avatar>
        
        <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
          {user.username}
        </Typography>
        
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 1 }}>
          {user.first_name || ''} {user.last_name || ''}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
          {user.email}
        </Typography>
        
        <Chip
          label={user.role_name}
          size="small"
          sx={{
            fontWeight: 500,
            ...getRoleColor(user.role_name)
          }}
        />
      </Box>
      
      <Box sx={{ p: 2, flexGrow: 1 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>{t('user.table.userId')}:</strong> {user.account_id}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>{t('user.table.branch')}:</strong> {user.branch_name}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          <strong>{t('menu.lastUpdated')}:</strong> {new Date().toLocaleDateString()}
        </Typography>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        p: 1,
        borderTop: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)'
      }}>
        <Tooltip title={t('user.editUser')}>
          <IconButton 
            color="primary" 
            size="small" 
            onClick={() => onEdit(user)}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title={t('user.resetPassword')}>
          <IconButton 
            color="warning" 
            size="small" 
            onClick={() => onResetPassword(user.account_id, user.email)}
          >
            <Email fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title={t('user.deleteUser')}>
          <IconButton 
            color="error" 
            size="small" 
            onClick={() => onDelete(user.account_id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
};

// Main component
const UserManagement = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const MotionDiv = motion.div;
  
  // View mode state (list or grid)
  const [viewMode, setViewMode] = useState('list');
  
  // State for users data
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: 'account_id',
    direction: 'asc'
  });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    message: '',
    title: t('user.success.title')
  });
  
  // Branches state
  const [branches, setBranches] = useState([]);
  
  // Status message states
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);
  
  // Fetch users and branches on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [response, branchesResponse] = await Promise.all([
          userService.getAllAccounts(),
          userService.getAllBranches()
        ]);
        
        setUsers(response || []);
        setBranches(branchesResponse || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('user.loadError'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [t]);

  // Apply filters and sorting whenever they change
  useEffect(() => {
    let filtered = [...users];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(term) || 
        user.email?.toLowerCase().includes(term) ||
        user.account_id?.toLowerCase().includes(term) ||
        user.first_name?.toLowerCase().includes(term) ||
        user.last_name?.toLowerCase().includes(term)
      );
    }
    
    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role_name === roleFilter);
    }
    
    // Apply branch filter
    if (branchFilter !== 'all') {
      filtered = filtered.filter(user => user.branch_id.toString() === branchFilter);
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredUsers(filtered);
    setPage(0);
  }, [users, searchTerm, roleFilter, branchFilter, sortConfig]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle role filter change
  const handleRoleChange = (e) => {
    setRoleFilter(e.target.value);
  };
  
  // Handle branch filter change
  const handleBranchChange = (e) => {
    setBranchFilter(e.target.value);
  };
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setBranchFilter('all');
  };
  
  // Handle column sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Handle edit user
  const handleEditUser = (user) => {
    const preparedUser = {
      ...user,
      role_id: user.role_id || getRoleId(user.role_name)
    };
    console.log("User prepared for edit:", preparedUser);
    setSelectedUser(preparedUser);
    setEditUserDialog(true);
  };
  
  // Handle user update
  const handleUpdateUser = async (userData) => {
    setIsLoading(true);
    
    try {
      const processedData = {
        ...userData,
        branch_id: userData.branch_id,
        role_id: userData.role_id
      };
      
      await userService.updateUserAdmin(processedData);
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.account_id === userData.account_id ? { 
            ...user, 
            ...userData,
            role_name: userData.role_id === '1' ? 'Admin' : 
                      userData.role_id === '2' ? 'Manager' : 'Staff'
          } : user
        )
      );
      
      // Force refresh notifications
      window.dispatchEvent(new CustomEvent('force-notification-refresh'));
      
      setSuccessDialog({
        open: true,
        message: t('user.updateSuccess'),
        title: t('user.success.userUpdated')
      });
      
      setEditUserDialog(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      setError(t('user.updateError') + ': ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle create user
  const handleCreateUser = async (userData) => {
    setIsLoading(true);
    
    try {
      await userService.createUser(userData);
      
      // Refresh the user list after creating a new user
      const updatedUsers = await userService.getAllAccounts();
      setUsers(updatedUsers || []);
      
      // Force refresh notifications
      window.dispatchEvent(new CustomEvent('force-notification-refresh'));
      
      setSuccessDialog({
        open: true,
        message: t('user.createSuccess'),
        title: t('user.success.userCreated')
      });
      
      setAddUserDialog(false);
    } catch (err) {
      console.error('Error creating user:', err);
      setError(t('user.createError') + ': ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUserToDelete) return;
    
    setIsLoading(true);
    try {
      await userService.deleteUser(selectedUserToDelete);
      
      setUsers(prev =>
        prev.filter(user => user.account_id !== selectedUserToDelete)
      );
      
      // Force refresh notifications
      window.dispatchEvent(new CustomEvent('force-notification-refresh'));
      
      setSuccessDialog({
        open: true,
        message: t('user.deleteSuccess'),
        title: t('user.success.userDeleted')
      });
      
      setDeleteDialog(false);
      setSelectedUserToDelete(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(t('user.deleteError'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle password reset
  const handlePasswordReset = async (accountId, email) => {
    setIsLoading(true);
    try {
      await userService.resetPassword(accountId, email);
      
      // Force refresh notifications
      window.dispatchEvent(new CustomEvent('force-notification-refresh'));
      
      setSuccessDialog({
        open: true,
        message: t('user.passwordResetSuccess'),
        title: t('user.success.passwordReset')
      });
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(t('user.passwordResetError'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Generate avatar initials
  const getInitials = (userName) => {
    return userName ? userName.charAt(0).toUpperCase() : '';
  };
  
  const getRoleId = (roleName) => {
    switch (roleName) {
      case 'Admin': return '1';
      case 'Manager': return '2';
      case 'Staff': return '3';
      default: return '';
    }
  };

  // Get role status color
  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin':
        return {
          bgcolor: theme.palette.error.light + '20',
          color: theme.palette.error.main
        };
      case 'Manager':
        return {
          bgcolor: theme.palette.warning.light + '20',
          color: theme.palette.warning.main
        };
      case 'Staff':
        return {
          bgcolor: theme.palette.success.light + '20',
          color: theme.palette.success.main
        };
      default:
        return {
          bgcolor: theme.palette.grey[100],
          color: theme.palette.grey[700]
        };
    }
  };
  
  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'list' ? 'grid' : 'list');
  };

  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {t('user.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('user.subtitle')}
            </Typography>
          </Box>
        </Box>
      </MotionDiv>
      
      {/* Status messages */}
      {successMessage && (
        <Zoom in={!!successMessage}>
          <Alert 
            severity="success" 
            onClose={() => setSuccessMessage('')}
            sx={{ 
              mb: 2, 
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 200, 83, 0.15)',
              '& .MuiAlert-icon': {
                color: '#10b981',
              }
            }}
          >
            <Typography variant="body1" fontWeight={500}>
              {successMessage}
            </Typography>
          </Alert>
        </Zoom>
      )}
      
      {error && (
        <Zoom in={!!error}>
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ 
              mb: 2, 
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
              '& .MuiAlert-icon': {
                color: '#f43f5e',
              }
            }}
          >
            <Typography variant="body1" fontWeight={500}>
              {error}
            </Typography>
          </Alert>
        </Zoom>
      )}
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  placeholder={t('user.searchPlaceholder')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('user.role')}</InputLabel>
                  <Select
                    value={roleFilter}
                    onChange={handleRoleChange}
                    label={t('user.role')}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="all">{t('user.allRoles')}</MenuItem>
                    <MenuItem value="Admin">{t('user.admin')}</MenuItem>
                    <MenuItem value="Manager">{t('user.manager')}</MenuItem>
                    <MenuItem value="Staff">{t('user.staff')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('user.branch')}</InputLabel>
                  <Select
                    value={branchFilter}
                    onChange={handleBranchChange}
                    label={t('user.branch')}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="all">{t('user.allBranches')}</MenuItem>
                    {branches.map(branch => (
                      <MenuItem key={branch.branch_id} value={branch.branch_id.toString()}>
                        {branch.location_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={2} justifyContent={isMobile ? "center" : "flex-start"}>
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleClearFilters}
                    startIcon={<FilterList />}
                    sx={{ borderRadius: 2 }}
                  >
                    {t('user.clear')}
                  </Button>
                  
                  <Tooltip title={viewMode === 'list' ? t('user.gridView') : t('user.listView')}>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={toggleViewMode}
                      startIcon={viewMode === 'list' ? <ViewModule /> : <ViewList />}
                      sx={{ borderRadius: 2 }}
                    >
                      {viewMode === 'list' ? t('user.grid') : t('user.list')}
                    </Button>
                  </Tooltip>
                  
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<Add />}
                    onClick={() => setAddUserDialog(true)}
                    sx={{ 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                      }
                    }}
                  >
                    {t('user.addUser')}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<Refresh />}
                    onClick={() => {
                      setIsLoading(true);
                      userService.getAllAccounts()
                        .then(response => {
                          setUsers(response || []);
                          setSuccessMessage(t('user.refreshSuccess'));
                        })
                        .catch(err => {
                          setError(t('user.refreshError'));
                          console.error(err);
                        })
                        .finally(() => {
                          setIsLoading(false);
                        });
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    {t('user.refresh')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>{t('user.loadingUsers')}</Typography>
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography>{t('user.noUsersFound')}</Typography>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  {t('user.clearFilters')}
                </Button>
              </Box>
            ) : viewMode === 'list' ? (
              // LIST VIEW
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          onClick={() => handleSort('account_id')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('user.table.userId')}
                            {sortConfig.key === 'account_id' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('username')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('user.table.username')}
                            {sortConfig.key === 'username' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('first_name')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('user.table.firstName')}
                            {sortConfig.key === 'first_name' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('last_name')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('user.table.lastName')}
                            {sortConfig.key === 'last_name' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          onClick={() => handleSort('email')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('user.table.email')}
                            {sortConfig.key === 'email' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          onClick={() => handleSort('branch_name')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('user.table.branch')}
                            {sortConfig.key === 'branch_name' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          onClick={() => handleSort('role_name')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('user.table.role')}
                            {sortConfig.key === 'role_name' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{t('user.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((user) => (
                          <TableRow 
                            hover 
                            key={user.account_id}
                            component={motion.tr}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {user.account_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  src={user.image}
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  }}
                                >
                                  {getInitials(user.username)}
                                </Avatar>
                                <Typography fontWeight={500}>{user.username}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{user.first_name || '-'}</TableCell>
                            <TableCell>{user.last_name || '-'}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip
                                label={user.branch_name}
                                size="small"
                                sx={{
                                  background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                                  color: '#4338ca',
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.role_name}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  ...getRoleColor(user.role_name)
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                <Tooltip title={t('user.editUser')}>
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() => handleEditUser(user)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                
                                <Tooltip title={t('user.resetPassword')}>
                                  <IconButton
                                    color="warning"
                                    size="small"
                                    onClick={() => handlePasswordReset(user.account_id, user.email)}
                                  >
                                    <Email fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                
                                <Tooltip title={t('user.deleteUser')}>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => {
                                      setSelectedUserToDelete(user.account_id);
                                      setDeleteDialog(true);
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredUsers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            ) : (
              // GRID VIEW
              <>
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user) => (
                        <Grid 
                          item 
                          key={user.account_id} 
                          xs={12} 
                          sm={6} 
                          md={4} 
                          lg={3}
                          component={motion.div}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <UserCard 
                            user={user} 
                            onEdit={handleEditUser}
                            onDelete={(id) => {
                              setSelectedUserToDelete(id);
                              setDeleteDialog(true);
                            }}
                            onResetPassword={handlePasswordReset}
                            t={t}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Box>
                
                <TablePagination
                  rowsPerPageOptions={[8, 16, 24, 32]}
                  component="div"
                  count={filteredUsers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </CardContent>
        </Card>
      </MotionDiv>
      
      {/* Add User Dialog */}
      <AddUserDialog
        open={addUserDialog}
        onClose={() => setAddUserDialog(false)}
        onSubmit={handleCreateUser}
        branches={branches}
        isLoading={isLoading}
      />
      
      {/* Edit User Dialog */}
      <EditUser
        open={editUserDialog}
        onClose={() => setEditUserDialog(false)}
        onSubmit={handleUpdateUser}
        user={selectedUser}
        branches={branches}
        isLoading={isLoading}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDeleteUser}
        title={t('user.delete.title')}
        message={t('user.delete.message')}
        loading={isLoading}
      />
      
      {/* Success Dialog */}
      <SuccessDialog
        open={successDialog.open}
        onClose={() => setSuccessDialog({ ...successDialog, open: false })}
        title={successDialog.title}
        message={successDialog.message}
      />
    </Box>
  );
};

export default UserManagement;